// react
import { useCallback, useState } from 'react';

// mui
import {
  Checkbox,
  FormControlLabel,
  FormGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

// types
import type { FactValue, ReviewFact } from '@karehero/models';

// components
import { Button } from '@/components/atoms/Button/Button';
import { RadioGroup } from '@/components/atoms/RadioGroup/RadioGroup';
import { BottomSheetDialog } from '@/pwa/atoms/BottomSheetDialog/BottomSheetDialog';

/** One value to save against a fact, or against one part of it. */
export interface FactEdit {
  id: string;
  value: FactValue;
}

export interface FactEditSheetProps {
  fact: ReviewFact;
  onSave: (edits: FactEdit[]) => void;
  onClose: () => void;
}

const DONE_LABEL = 'Done';

const initialValues = (fact: ReviewFact): Record<string, string> =>
  fact.control === 'parts'
    ? Object.fromEntries((fact.parts ?? []).map((p) => [p.id, p.value]))
    : { [fact.id]: fact.value ?? '' };

/** The fields a fact is typed into: one per part, or just the one. */
const fieldsOf = (fact: ReviewFact): { id: string; label: string }[] =>
  fact.control === 'parts'
    ? (fact.parts ?? [])
    : [{ id: fact.id, label: fact.label }];

/**
 * FactEditSheet edits one fact in a bottom sheet, with the control its answer
 * needs. Picking a choice saves it; anything else is saved with Done.
 */
export const FactEditSheet = ({
  fact,
  onSave,
  onClose,
}: FactEditSheetProps) => {
  const [values, setValues] = useState(() => initialValues(fact));
  const [selected, setSelected] = useState(() => fact.selected ?? []);

  const handleChange = useCallback(
    (id: string, value: string) =>
      setValues((prev) => ({ ...prev, [id]: value })),
    [],
  );

  const handleToggle = useCallback(
    (value: string) =>
      setSelected((prev) =>
        prev.includes(value)
          ? prev.filter((v) => v !== value)
          : [...prev, value],
      ),
    [],
  );

  const handleDone = useCallback(
    () =>
      onSave(
        fact.control === 'choices'
          ? [{ id: fact.id, value: selected }]
          : Object.entries(values).map(([id, value]) => ({ id, value })),
      ),
    [fact, onSave, selected, values],
  );

  const valueOf = (id: string) => values[id] ?? '';

  const renderControl = () => {
    switch (fact.control) {
      case 'choice':
        return (
          <RadioGroup
            ariaLabel={fact.label}
            isHideTitle
            maxColumns={1}
            options={fact.choices}
            value={fact.selected?.[0] ?? ''}
            onChange={(value) => onSave([{ id: fact.id, value: [value] }])}
          />
        );
      case 'choices':
        return (
          <FormGroup aria-label={fact.label}>
            {fact.choices?.map((choice) => (
              <FormControlLabel
                key={choice.value}
                label={choice.label}
                control={
                  <Checkbox
                    checked={selected.includes(choice.value)}
                    onChange={() => handleToggle(choice.value)}
                  />
                }
              />
            ))}
          </FormGroup>
        );
      default:
        return (
          <Stack spacing={2}>
            {fieldsOf(fact).map((field, index) => (
              <TextField
                key={field.id}
                autoFocus={index === 0}
                fullWidth
                size='small'
                type={fact.control === 'date' ? 'date' : 'text'}
                label={field.label}
                slotProps={
                  fact.control === 'date'
                    ? { inputLabel: { shrink: true } }
                    : undefined
                }
                value={valueOf(field.id)}
                onChange={(e) => handleChange(field.id, e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleDone()}
              />
            ))}
          </Stack>
        );
    }
  };

  return (
    <BottomSheetDialog
      isOpen
      onClose={onClose}
      title={
        <Typography variant='h4' component='span'>
          {fact.label}
        </Typography>
      }
      maxWidth='xs'
      footer={
        fact.control !== 'choice' && (
          <Button
            variant='primary'
            size='lg'
            isFullWidth
            ariaLabel={DONE_LABEL}
            onPress={handleDone}
          >
            {DONE_LABEL}
          </Button>
        )
      }
    >
      {renderControl()}
    </BottomSheetDialog>
  );
};

FactEditSheet.displayName = 'FactEditSheet';
export default FactEditSheet;
